import React, { useState, useEffect, useRef } from "react";
import { Modal } from "react-bootstrap";
import { getAlert } from "../../services/appAlerts";

const DefaultDocumentsModal = ({
  show,
  onHide,
  defaultDocuments,
  onSave,
  reqDocuments = [],
  dataTypeMappings = [],
  ddrmsDocs = [],
  performerType = "",
  renderPurchaseOrder,
  renderGenerate,
  renderUploadReference
}) => {
  const [activeTab, setActiveTab] = useState("documents"); // "documents" | "action" | "add"
  const [tempDocuments, setTempDocuments] = useState([]);
  const [selectedDocsToLoad, setSelectedDocsToLoad] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [actionTab, setActionTab] = useState(null);
  const actionTabOpenRef = useRef(false);
  const [bulkItemStatuses, setBulkItemStatuses] = useState({});
  const [stagedReferenceDocs, setStagedReferenceDocs] = useState({});
  const [stagedGeneratedDocs, setStagedGeneratedDocs] = useState({});
  const [bulkProcess, setBulkProcess] = useState({
    active: false,
    items: [],
    currentIndex: 0,
    completed: 0,
    total: 0,
    status: "idle"
  });

  // Form State
  const [newDocName, setNewDocName] = useState("");
  const [newDocAction, setNewDocAction] = useState("upload"); // "upload" | "generate"
  const [newDdrmsDocId, setNewDdrmsDocId] = useState("");
  const [newMappingId, setNewMappingId] = useState("");
  const [newReqDocId, setNewReqDocId] = useState("");

  const getDocumentAction = (doc) => {
    if (
      doc?.purchaseOrderType ||
      String(doc?.name || "").trim().toLowerCase().startsWith("purchase order")
    ) {
      return "generate";
    }
    const configuredAction = String(doc?.action ?? doc?.Action ?? "").trim().toLowerCase();
    if (configuredAction === "generate" || configuredAction === "upload") {
      return configuredAction;
    }
    return doc?.ddrmsDocId && doc?.mappingId ? "generate" : "upload";
  };
  const isGenerateDocument = (doc) => getDocumentAction(doc) === "generate";
  const isGenerateReady = (doc) =>
    isGenerateDocument(doc) && Boolean(doc?.ddrmsDocId && doc?.mappingId);
  const isPurchaseOrderDocument = (doc) =>
    Boolean(doc?.purchaseOrderType) ||
    String(doc?.name || "").trim().toLowerCase().startsWith("purchase order");
  const isBulkEligible = (doc) =>
    isPurchaseOrderDocument(doc)
      ? true
      : isGenerateDocument(doc)
        ? isGenerateReady(doc)
        : Boolean(doc?.reqDocId);
  const getBulkItemKey = (type, doc, idx) =>
    `${type}:${idx !== undefined ? idx : (doc?.ddrmsDocId || doc?.reqDocId || doc?.name)}`;
  const getReferenceKey = (doc) => String(doc?.reqDocId || doc?.name || "");
  const getGeneratedKey = (doc) => String(doc?.ddrmsDocId || doc?.name || "");
  const getStagedPurchaseOrder = (doc) =>
    isPurchaseOrderDocument(doc) ? stagedGeneratedDocs[getGeneratedKey(doc)] || null : null;
  const handleRemoveStagedPurchaseOrder = (doc, idx) => {
    if (bulkProcess.active) return;
    const generatedKey = getGeneratedKey(doc);
    setStagedGeneratedDocs((current) => {
      const updated = { ...current };
      delete updated[generatedKey];
      return updated;
    });
    setBulkItemStatuses((current) => {
      const updated = { ...current };
      delete updated[getBulkItemKey("purchaseOrder", doc, idx)];
      return updated;
    });
    setSelectedDocsToLoad((current) => current.filter((selectedIdx) => selectedIdx !== idx));
  };
  const getGenerateRenderOptions = (doc, autoProcess) => {
    const generatedKey = getGeneratedKey(doc);
    return {
      autoProcess,
      stagedGenerated: stagedGeneratedDocs[generatedKey] || null,
      onStageGenerated: (generatedData) => {
        setStagedGeneratedDocs((current) => ({ ...current, [generatedKey]: generatedData }));
        if (isPurchaseOrderDocument(doc)) {
          const docIndex = tempDocuments.indexOf(doc);
          if (docIndex >= 0) {
            setSelectedDocsToLoad((current) => [...new Set([...current, docIndex])]);
          }
        }
      }
    };
  };
  const getUploadRenderOptions = (doc, autoProcess) => {
    const referenceKey = getReferenceKey(doc);
    return {
      autoProcess,
      stagedReference: stagedReferenceDocs[referenceKey] || null,
      onStageReference: (referenceData) => {
        setStagedReferenceDocs((current) => ({ ...current, [referenceKey]: referenceData }));
      }
    };
  };

  useEffect(() => {
    if (!show || actionTabOpenRef.current) return;

    if (defaultDocuments && defaultDocuments.length > 0) {
      setTempDocuments(JSON.parse(JSON.stringify(defaultDocuments)));
      setSelectedDocsToLoad(
        defaultDocuments
          .map((doc, i) => ({ doc, i }))
          .filter(({ doc }) => isBulkEligible(doc))
          .map(({ i }) => i)
      );
    } else {
      setTempDocuments([]);
      setSelectedDocsToLoad([]);
    }
    setEditIndex(null);
    setActionTab(null);
    setBulkItemStatuses({});
    setStagedReferenceDocs({});
    setStagedGeneratedDocs({});
    actionTabOpenRef.current = false;
    setBulkProcess({ active: false, items: [], currentIndex: 0, completed: 0, total: 0, status: "idle" });
    setActiveTab("documents");
  }, [defaultDocuments, show]);

  const handleToggleTempDoc = (idx) => {
    if (bulkProcess.active) return;
    if (!isBulkEligible(tempDocuments[idx])) return;

    if (selectedDocsToLoad.includes(idx)) {
      setSelectedDocsToLoad(selectedDocsToLoad.filter((i) => i !== idx));
    } else {
      setSelectedDocsToLoad([...selectedDocsToLoad, idx]);
    }
  };

  const handleDeleteDoc = (idxToDelete) => {
    if (bulkProcess.active) return;

    const updated = tempDocuments.filter((_, i) => i !== idxToDelete);
    setTempDocuments(updated);

    // Update selected indices list
    const newSelected = selectedDocsToLoad
      .filter((i) => i !== idxToDelete)
      .map((i) => (i > idxToDelete ? i - 1 : i));
    setSelectedDocsToLoad(newSelected);

    if (editIndex === idxToDelete) {
      setEditIndex(null);
      setNewDocName("");
      setNewDdrmsDocId("");
      setNewMappingId("");
      setNewReqDocId("");
    } else if (editIndex > idxToDelete) {
      setEditIndex(editIndex - 1);
    }
  };

  const handleEditDocClick = (idx) => {
    if (bulkProcess.active) return;

    const doc = tempDocuments[idx];
    setEditIndex(idx);
    setNewDocName(doc.name || "");
    setNewDocAction(getDocumentAction(doc));
    setNewDdrmsDocId(doc.ddrmsDocId !== null && doc.ddrmsDocId !== undefined ? String(doc.ddrmsDocId) : "");
    setNewMappingId(doc.mappingId !== null && doc.mappingId !== undefined ? String(doc.mappingId) : "");
    setNewReqDocId(doc.reqDocId !== null && doc.reqDocId !== undefined ? String(doc.reqDocId) : "");
    setActiveTab("add");
  };

  const handleSaveDoc = (e) => {
    e.preventDefault();
    if (!newDocName.trim()) return;

    const existingDoc = editIndex !== null ? tempDocuments[editIndex] : null;
    const targetDoc = isPurchaseOrderDocument(existingDoc)
      ? { ...existingDoc, name: newDocName.trim() }
      : {
        name: newDocName.trim(),
        action: newDocAction,
        ddrmsDocId: newDocAction === "generate" ? (parseInt(newDdrmsDocId) || null) : null,
        mappingId: newDocAction === "generate" ? (parseInt(newMappingId) || null) : null,
        reqDocId: newDocAction === "upload" ? (parseInt(newReqDocId) || null) : null,
      };

    const updated = [...tempDocuments];
    if (editIndex !== null) {
      updated[editIndex] = targetDoc;
      setSelectedDocsToLoad((current) => {
        const withoutEditedDoc = current.filter((idx) => idx !== editIndex);
        return isBulkEligible(targetDoc)
          ? [...new Set([...withoutEditedDoc, editIndex])]
          : withoutEditedDoc;
      });
      setEditIndex(null);
    } else {
      updated.push(targetDoc);
      if (isBulkEligible(targetDoc)) {
        setSelectedDocsToLoad([...selectedDocsToLoad, updated.length - 1]);
      }
    }

    setTempDocuments(updated);

    // Reset Form Fields
    setNewDocName("");
    setNewDdrmsDocId("");
    setNewMappingId("");
    setNewReqDocId("");

    setActiveTab("documents");
  };

  const handleSave = () => {
    if (bulkProcess.active) return;
    onSave(tempDocuments);
  };

  const handleOpenActionTab = (type, doc, bulk = false) => {
    if (bulkProcess.active && !bulk) return;

    actionTabOpenRef.current = true;
    setActionTab({ type, doc, bulk });
    setActiveTab(bulk ? "documents" : "action");
  };

  const handleCloseActionTab = (completed = false) => {
    if (actionTab?.bulk && bulkProcess.active) {
      if (!completed) {
        actionTabOpenRef.current = false;
        setActionTab(null);
        setBulkProcess((current) => ({ ...current, active: false, status: "cancelled" }));
        setActiveTab("documents");
        return;
      }

      const completedCount = bulkProcess.currentIndex + 1;
      const nextIndex = bulkProcess.currentIndex + 1;
      const completedItem = bulkProcess.items[bulkProcess.currentIndex];
      if (completedItem) {
        const completedPurchaseOrder = completedItem.type === "purchaseOrder"
          ? stagedGeneratedDocs[getGeneratedKey(completedItem.doc)]
          : null;
        setBulkItemStatuses((current) => ({
          ...current,
          [getBulkItemKey(completedItem.type, completedItem.doc, completedItem.idx)]:
            completedItem.type === "upload" || completedPurchaseOrder?.kind === "uploaded"
              ? "uploaded"
              : "generated"
        }));
      }
      if (nextIndex < bulkProcess.items.length) {
        const nextItem = bulkProcess.items[nextIndex];
        setBulkProcess((current) => ({
          ...current,
          currentIndex: nextIndex,
          completed: completedCount
        }));
        setActionTab({ ...nextItem, bulk: true });
        setActiveTab("documents");
        return;
      }

      actionTabOpenRef.current = false;
      setActionTab(null);
      setBulkProcess((current) => ({
        ...current,
        active: false,
        completed: completedCount,
        status: "complete"
      }));
      getAlert("", "All selected documents uploaded successfully", "success");
      setActiveTab("documents");
      return;
    }

    actionTabOpenRef.current = false;
    setActionTab(null);
    setActiveTab("documents");
  };

  const handleStartBulkUpload = () => {
    const selectedItems = tempDocuments
      .map((doc, idx) => ({
        doc,
        idx,
        type: isPurchaseOrderDocument(doc) ? "purchaseOrder" : getDocumentAction(doc)
      }))
      .filter((item) => selectedDocsToLoad.includes(item.idx) && isBulkEligible(item.doc))
      .sort((a, b) => {
        const processOrder = { generate: 0, purchaseOrder: 1, upload: 2 };
        return a.type === b.type
          ? a.idx - b.idx
          : processOrder[a.type] - processOrder[b.type];
      });

    if (selectedItems.length === 0) return;

    const items = selectedItems.map(({ doc, type, idx }) => ({ doc, type, idx }));
    setBulkItemStatuses({});
    setBulkProcess({
      active: true,
      items,
      currentIndex: 0,
      completed: 0,
      total: items.length,
      status: "running"
    });
    handleOpenActionTab(items[0].type, items[0].doc, true);
  };

  const bulkProgressPercent = bulkProcess.total > 0
    ? Math.round((bulkProcess.completed / bulkProcess.total) * 100)
    : 0;
  const isProcessing = bulkProcess.active;

  // Segregate documents
  const generateDocs = tempDocuments
    .map((doc, idx) => ({ doc, idx }))
    .filter((item) => isGenerateDocument(item.doc));
  const eligibleGenerateDocs = generateDocs.filter((item) => isBulkEligible(item.doc));

  const uploadDocs = tempDocuments
    .map((doc, idx) => ({ doc, idx }))
    .filter((item) => !isGenerateDocument(item.doc));
  const eligibleUploadDocs = uploadDocs.filter((item) => Boolean(item.doc.reqDocId));

  // Segregated Select All Handlers
  const allGenerateSelected = eligibleGenerateDocs.length > 0 && eligibleGenerateDocs.every(item => selectedDocsToLoad.includes(item.idx));
  const handleToggleAllGenerate = () => {
    if (bulkProcess.active) return;
    const generateIndices = eligibleGenerateDocs.map(item => item.idx);
    if (allGenerateSelected) {
      setSelectedDocsToLoad(selectedDocsToLoad.filter(i => !generateIndices.includes(i)));
    } else {
      setSelectedDocsToLoad([...new Set([...selectedDocsToLoad, ...generateIndices])]);
    }
  };

  const allUploadSelected = eligibleUploadDocs.length > 0 && eligibleUploadDocs.every(item => selectedDocsToLoad.includes(item.idx));
  const handleToggleAllUpload = () => {
    if (bulkProcess.active) return;
    const uploadIndices = eligibleUploadDocs.map(item => item.idx);
    if (allUploadSelected) {
      setSelectedDocsToLoad(selectedDocsToLoad.filter(i => !uploadIndices.includes(i)));
    } else {
      setSelectedDocsToLoad([...new Set([...selectedDocsToLoad, ...uploadIndices])]);
    }
  };
  const editingPurchaseOrder =
    editIndex !== null && isPurchaseOrderDocument(tempDocuments[editIndex]);

  return (
    <Modal
      show={show}
      size="xl"
      centered
      onHide={isProcessing ? undefined : onHide}
      dialogClassName="crd-modal"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Body className="p-0">
        <style>{`
          .crd-shell,
          .crd-shell button,
          .crd-shell input,
          .crd-shell select,
          .crd-shell textarea {
              font-family: inherit !important;
          }
          .modal-backdrop {
              z-index: 1080 !important;
          }
          .modal {
              z-index: 1090 !important;
          }
          .modal-backdrop.show {
              backdrop-filter: blur(8px) !important;
              -webkit-backdrop-filter: blur(8px) !important;
              background-color: rgba(15, 23, 42, 0.45) !important;
              opacity: 1 !important;
          }
          .modal.fade .crd-modal.modal-dialog {
              transform: scale(0.95) !important;
              transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
          }
          .modal.show .crd-modal.modal-dialog {
              transform: scale(1) !important;
          }
          .crd-modal {
              max-width: 90% !important;
              width: 1200px !important;
          }
          .crd-modal .modal-content {
              border: 0;
              border-radius: 16px;
              overflow: hidden;
              background: #ffffff;
              box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
              height: auto;
              max-height: calc(100vh - 60px);
              display: flex;
              flex-direction: column;
          }
          .crd-modal .modal-body {
              display: flex;
              flex: 1 1 auto;
              min-height: 0;
              overflow: hidden;
          }
          .crd-shell {
              background: #ffffff;
              display: flex;
              flex-direction: column;
              min-height: 0;
              flex: 1;
              overflow: hidden;
          }
          .crd-header {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 18px;
              padding: 22px 24px 18px;
              background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
              border-bottom: 1px solid #f1f5f9;
          }
          .crd-header-left {
              display: flex;
              align-items: flex-start;
              gap: 14px;
              min-width: 0;
              flex: 1;
              text-align: left;
          }
          .crd-icon-box {
              width: 44px;
              height: 44px;
              border-radius: 12px;
              background: linear-gradient(135deg, #0f766e 0%, #2563eb 100%);
              display: flex;
              align-items: center;
              justify-content: center;
              color: #ffffff;
              font-size: 18px;
              flex-shrink: 0;
              box-shadow: 0 8px 16px rgba(37, 99, 235, 0.2);
          }
          .crd-title { font-size: 18px; font-weight: 800; color: #0f172a; line-height: 1.2; margin: 0; }
          .crd-subtitle { font-size: 13px; color: #64748b; margin: 6px 0 0; }
          .crd-close-btn {
              width: 38px;
              height: 38px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              border-radius: 8px;
              border: 1px solid #cbd5e1;
              background: #ffffff;
              color: #475569;
              cursor: pointer;
              transition: all 0.15s ease;
              flex-shrink: 0;
          }
          .crd-close-btn:hover { background: #f8fafc; border-color: #94a3b8; color: #0f172a; transform: translateY(-1px); }
          
          .crd-body {
              padding: 24px;
              background: #ffffff;
              overflow-y: auto;
              overscroll-behavior: contain;
              scrollbar-gutter: stable;
              min-height: 0;
              flex: 1;
          }
          .crd-body::-webkit-scrollbar { width: 8px; }
          .crd-body::-webkit-scrollbar-track { background: #f1f5f9; }
          .crd-body::-webkit-scrollbar-thumb {
              border: 2px solid #f1f5f9;
              border-radius: 999px;
              background: #94a3b8;
          }
          .crd-body::-webkit-scrollbar-thumb:hover { background: #64748b; }

          /* Tabs Styling matched with Document.jsx */
          .crd-tabs-container {
              margin-bottom: 20px !important;
              position: relative !important;
          }
          .crd-tabs-list {
              display: flex !important;
              align-items: stretch !important;
              gap: 4px !important;
              margin: 0 !important;
              padding: 8px 4px 0 !important;
              list-style: none !important;
              width: 100% !important;
              border-bottom: 2px solid #f1f5f9 !important;
              position: relative !important;
          }
          .crd-tab-item {
              position: relative !important;
              display: inline-flex !important;
              align-items: center !important;
              gap: 7px !important;
              border: none !important;
              background: transparent !important;
              padding: 9px 16px 11px !important;
              font-size: 13.5px !important;
              font-weight: 600 !important;
              color: #64748b !important;
              cursor: pointer !important;
              border-radius: 8px 8px 0 0 !important;
              transition: color 0.2s ease, background 0.2s ease !important;
              outline: none !important;
              box-shadow: none !important;
              white-space: nowrap !important;
              user-select: none !important;
          }
          .crd-tab-item:focus,
          .crd-tab-item:active,
          .crd-tab-item:focus-visible {
              outline: none !important;
              border: none !important;
              box-shadow: none !important;
          }
          .crd-tab-item::after {
              content: "" !important;
              position: absolute !important;
              bottom: -2px !important;
              left: 0 !important;
              right: 0 !important;
              height: 2px !important;
              background: #2563eb !important;
              border-radius: 2px 2px 0 0 !important;
              transform: scaleX(0) !important;
              transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1) !important;
          }
          .crd-tab-item:hover {
              color: #1e293b !important;
              background: rgba(37, 99, 235, 0.04) !important;
          }
          .crd-tab-item.active {
              color: #2563eb !important;
              font-weight: 700 !important;
              background: transparent !important;
          }
          .crd-tab-item.active:hover {
              background: transparent !important;
          }
          .crd-tab-item.active::after {
              transform: scaleX(1) !important;
          }
          .crd-tab-item i {
              font-size: 13px !important;
              transition: color 0.2s ease !important;
              color: #64748b !important;
          }
          .crd-tab-item:hover i {
              color: #1e293b !important;
          }
          .crd-tab-item.active i {
              color: #2563eb !important;
          }
          .crd-tab-count {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              min-width: 20px;
              height: 20px;
              padding: 0 6px;
              border-radius: 999px;
              background: #e2e8f0;
              color: #475569;
              font-size: 11px;
              font-weight: 700;
          }
          .crd-tab-item.active .crd-tab-count {
              background: #dbeafe;
              color: #2563eb;
          }
          .crd-tab-close {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 20px;
              height: 20px;
              margin-left: 2px;
              border-radius: 50%;
              color: #64748b;
          }
          .crd-tab-close:hover {
              background: #fee2e2;
              color: #dc2626;
          }
          .crd-tab-close i {
              color: inherit !important;
              font-size: 11px !important;
          }
          .crd-action-tab-pane {
              min-height: 420px;
              animation: crdFadeIn 0.2s ease;
          }
          .crd-action-tab-pane .gc-embedded,
          .crd-action-tab-pane .uo-embedded {
              width: 100%;
          }
          .crd-action-tab-pane .gc-shell,
          .crd-action-tab-pane .uo-shell {
              border: 1px solid #e2e8f0;
              border-radius: 12px;
              overflow: hidden;
              box-shadow: none;
          }
          .crd-action-tab-pane .gc-body-only .gc-shell,
          .crd-action-tab-pane .uo-body-only .uo-shell {
              border: 0;
              border-radius: 0;
              overflow: visible;
              box-shadow: none;
          }
          .crd-action-tab-pane .pop-container {
              margin: 0;
              border: 0;
              border-radius: 0;
              box-shadow: none;
              overflow: visible;
          }
          .crd-action-body-toolbar {
              display: flex;
              align-items: center;
              justify-content: flex-end;
              padding: 0 24px;
              box-sizing: border-box;
              margin-bottom: 12px;
          }
          .crd-background-processor {
              position: fixed;
              top: 0;
              left: -12000px;
              width: 1200px;
              pointer-events: none;
              z-index: -1;
          }
          .crd-row-status {
              display: inline-flex;
              align-items: center;
              gap: 5px;
              padding: 5px 9px;
              border: 1px solid #bbf7d0;
              border-radius: 999px;
              background: #f0fdf4;
              color: #15803d;
              font-size: 11.5px;
              font-weight: 700;
          }
          .crd-row-status.staged {
              border-color: #bfdbfe;
              background: #eff6ff;
              color: #1d4ed8;
          }
          .crd-bulk-progress {
              margin: 0 0 16px;
              padding: 14px 16px;
              border: 1px solid #dbeafe;
              border-radius: 10px;
              background: #f8fbff;
          }
          .crd-bulk-progress-head {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 12px;
              margin-bottom: 9px;
              color: #334155;
              font-size: 12.5px;
              font-weight: 700;
          }
          .crd-bulk-progress-native {
              display: block;
              width: 100%;
              height: 8px;
              overflow: hidden;
              appearance: none;
              border: 0;
              border-radius: 999px;
              background: #e2e8f0;
          }
          .crd-bulk-progress-native::-webkit-progress-bar {
              border-radius: 999px;
              background: #e2e8f0;
          }
          .crd-bulk-progress-native::-webkit-progress-value {
              border-radius: 999px;
              background: #64748b;
              transition: width 0.3s ease;
          }
          .crd-bulk-progress-native::-moz-progress-bar {
              border-radius: 999px;
              background: #64748b;
              transition: width 0.3s ease;
          }
          .crd-bulk-progress.running .crd-bulk-progress-native::-webkit-progress-value {
              background-image: linear-gradient(45deg, rgba(255,255,255,.25) 25%, transparent 25%, transparent 50%, rgba(255,255,255,.25) 50%, rgba(255,255,255,.25) 75%, transparent 75%, transparent);
              background-size: 16px 16px;
              animation: crdProgressStripe 0.8s linear infinite;
          }
          .crd-bulk-progress.complete {
              border-color: #bbf7d0;
              background: #f0fdf4;
          }
          .crd-bulk-progress.complete .crd-bulk-progress-native::-webkit-progress-value {
              background: #22c55e;
          }
          .crd-bulk-progress.complete .crd-bulk-progress-native::-moz-progress-bar {
              background: #22c55e;
          }
          .crd-bulk-progress.cancelled {
              border-color: #fed7aa;
              background: #fff7ed;
          }
          @keyframes crdProgressStripe {
              from { background-position: 16px 0; }
              to { background-position: 0 0; }
          }

          .crd-footer {
              padding: 16px 24px;
              background: #f8fafc;
              border-top: 1px solid #e2e8f0;
              display: flex;
              align-items: center;
              justify-content: flex-end;
              gap: 10px;
              flex-shrink: 0;
          }
          .crd-btn {
              min-height: 38px;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              padding: 8px 16px;
              border-radius: 8px;
              font-size: 13px;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
              white-space: nowrap;
              border: 1px solid #cbd5e1;
              background: #ffffff;
              color: #475569;
              outline: none;
          }
          .crd-btn:hover:not(:disabled) {
              background: #f8fafc;
              color: #0f172a;
              border-color: #94a3b8;
              transform: translateY(-1px);
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
          }
          .crd-btn:disabled {
              opacity: 0.6;
              cursor: not-allowed;
          }
          .crd-btn.primary {
              background: #2563eb;
              border-color: #2563eb;
              color: #ffffff;
          }
          .crd-btn.primary:hover:not(:disabled) {
              background: #1d4ed8;
              border-color: #1d4ed8;
              color: #ffffff;
              transform: translateY(-1px);
              box-shadow: 0 4px 10px -1px rgba(37, 99, 235, 0.25);
          }
          .crd-btn-ghost-danger {
              background: rgba(239, 68, 68, 0.06);
              border-color: rgba(239, 68, 68, 0.18);
              color: #dc2626;
          }
          .crd-btn-ghost-danger:hover {
              background: rgba(239, 68, 68, 0.12);
              border-color: rgba(239, 68, 68, 0.28);
              color: #b91c1c;
              transform: translateY(-1px);
              box-shadow: 0 4px 6px -1px rgba(239, 68, 68, 0.1);
          }
          .crd-btn-ghost-indigo {
              background: #ffffff;
              border-color: #cbd5e1;
              color: #475569;
          }
          .crd-btn-ghost-indigo:hover {
              background: #f8fafc;
              color: #0f172a;
              border-color: #94a3b8;
              transform: translateY(-1px);
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
          }

          /* Card-style list rows matching Document.jsx card UI */
          .pdm-doc-list {
              display: flex;
              flex-direction: column;
              gap: 12px;
          }
          .pdm-doc-row {
              display: grid;
              align-items: center;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 14px 16px;
              gap: 12px;
              transition: box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
              position: relative;
              background: #ffffff;
          }
          .pdm-doc-row:hover {
              border-color: #cbd5e1;
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
              transform: translateY(-1px);
              z-index: 2;
          }
          .pdm-doc-row-card {
              grid-template-columns: 34px minmax(0, 1fr) auto;
              align-items: center;
              padding: 16px;
              border-color: #dbe3ef;
              border-radius: 10px;
              box-shadow: 0 1px 2px rgba(15, 23, 42, 0.03);
          }
          .pdm-doc-row-card:hover {
              border-color: #bfdbfe;
              box-shadow: 0 8px 20px rgba(37, 99, 235, 0.08);
          }
          .pdm-doc-file {
              display: flex;
              align-items: center;
              gap: 12px;
              min-width: 0;
          }
          .pdm-doc-file-icon {
              width: 36px;
              height: 36px;
              border-radius: 8px;
              background: #eff6ff;
              color: #2563eb;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              flex: 0 0 36px;
              font-size: 16px;
          }
          .pdm-doc-file-main {
              min-width: 0;
              text-align: left;
          }
          .pdm-doc-title-row {
              display: flex;
              align-items: center;
              gap: 8px;
              flex-wrap: wrap;
          }
          .pdm-doc-required-label {
              color: #334155;
              font-size: 13px;
              font-weight: 600;
              line-height: 1.3;
              white-space: normal;
          }
          .pdm-doc-meta {
              display: flex;
              align-items: center;
              gap: 10px;
              margin-top: 4px;
              flex-wrap: wrap;
              font-size: 12px;
              color: #94a3b8;
          }
          
          /* Custom Checkboxes */
          .crd-checkbox-wrap {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              position: relative;
              width: 18px;
              height: 18px;
              flex-shrink: 0;
          }
          .crd-checkbox-custom {
              width: 18px;
              height: 18px;
              border: 2px solid #cbd5e1;
              border-radius: 4px;
              transition: all 0.2s ease;
              display: flex;
              align-items: center;
              justify-content: center;
          }
          .crd-checkbox-custom::after {
              content: "";
              display: none;
              width: 5px;
              height: 9px;
              border: solid white;
              border-width: 0 2px 2px 0;
              transform: rotate(45deg);
              margin-bottom: 2px;
          }
          .crd-checkbox-row.selected .crd-checkbox-custom {
              background-color: #2563eb;
              border-color: #2563eb;
          }
          .crd-checkbox-row.selected .crd-checkbox-custom::after {
              display: block;
          }

          .section-heading {
              font-size: 13px;
              font-weight: 800;
              color: #475569;
              text-transform: uppercase;
              letter-spacing: 0.05em;
              display: flex;
              align-items: center;
              gap: 8px;
          }
          .section-heading i {
              color: #2563eb;
          }
          
          /* Form groups in tab */
          .crd-form-group {
              display: flex;
              flex-direction: column;
              gap: 6px;
              margin-bottom: 16px;
          }
          .crd-label {
              font-size: 11px;
              font-weight: 800;
              color: #475569;
              text-transform: uppercase;
              letter-spacing: 0.05em;
              margin: 0;
          }
          .crd-section-panel {
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 12px;
              padding: 20px;
              margin-bottom: 24px;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
          }
          .crd-body input.form-control,
          .crd-body select.form-control {
              height: 40px;
              border-radius: 8px;
              border: 1px solid #cbd5e1;
              background-color: #ffffff;
              color: #0f172a;
              font-size: 13.5px;
              font-weight: 500;
              transition: all 0.2s ease;
              box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
          }
          .crd-body input.form-control:focus,
          .crd-body select.form-control:focus {
              border-color: #2563eb;
              box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
              background-color: #ffffff;
              outline: none;
          }
          .crd-meta-row { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 12px; }
          .crd-meta-pill {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              padding: 4px 10px;
              border-radius: 99px;
              background: #ffffff;
              border: 1px solid #e2e8f0;
              color: #475569;
              font-size: 12px;
              font-weight: 700;
          }
          .crd-meta-pill i { font-size: 11px; color: #2563eb; }
          .crd-section-head {
              display: flex;
              align-items: center;
              justify-content: space-between;
              margin-bottom: 16px;
          }
          .crd-section-heading-reset { margin: 0; }
          .crd-select-all {
              cursor: pointer;
              display: inline-flex;
              align-items: center;
              gap: 8px;
              color: #475569;
              font-size: 12px;
              font-weight: 700;
          }
          .crd-select-all.disabled { cursor: not-allowed; opacity: 0.45; }
          .crd-doc-check-center { display: flex; align-items: center; }
          .crd-checkbox-trigger {
              cursor: pointer;
              display: inline-flex;
              align-items: center;
              justify-content: center;
          }
          .crd-checkbox-trigger.disabled { cursor: not-allowed; opacity: 0.45; }
          .crd-not-linked { color: #94a3b8; font-style: italic; }
          .crd-doc-actions {
              margin-left: auto;
              display: flex;
              align-items: center;
              gap: 8px;
          }
          .crd-action-btn { min-height: 38px; }
          .crd-icon-action-btn {
              width: 38px;
              min-height: 38px;
              padding: 0;
              justify-content: center;
          }
          .crd-empty-config { padding: 32px 0; color: #94a3b8; text-align: center; }
          .crd-empty-config-icon {
              display: block;
              margin-bottom: 8px;
              color: #cbd5e1;
              font-size: 28px;
          }
          .crd-empty-config-text { font-size: 13px; font-style: italic; }
          .crd-add-tab { padding: 4px 0; text-align: left; }
          .crd-add-panel { margin: 0 auto; }
          .crd-add-heading { margin-top: 0; margin-bottom: 8px; }
          .crd-add-intro { margin-bottom: 24px; color: #64748b; font-size: 13px; }
          .crd-add-form { display: flex; flex-direction: column; gap: 20px; }
          .crd-add-main-grid {
              display: grid;
              grid-template-columns: 7fr 3fr;
              align-items: start;
              gap: 16px;
          }
          .crd-add-main-grid.name-only { grid-template-columns: 1fr; }
          .crd-form-group-compact { margin-bottom: 0; }
          .crd-form-help {
              display: block;
              margin-bottom: 4px;
              color: #64748b;
              font-size: 11px;
          }
          .crd-form-control-compact { padding: 8px 12px; font-size: 13.5px; }
          .crd-linked-fields-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 16px;
              padding-top: 16px;
              border-top: 1px dashed #e2e8f0;
          }
          .crd-linked-field-section {
              padding-top: 16px;
              border-top: 1px dashed #e2e8f0;
          }
          .crd-required-doc-field { max-width: 50%; margin-bottom: 0; }
          .crd-add-actions {
              display: flex;
              align-items: center;
              gap: 10px;
              margin-top: 14px;
              padding-top: 16px;
              border-top: 1px solid #e2e8f0;
          }
          .crd-submit-btn { min-height: 38px; padding: 8px 24px; }
          @media (max-width: 768px) {
              .crd-add-main-grid,
              .crd-linked-fields-grid { grid-template-columns: 1fr; }
              .crd-required-doc-field { max-width: 100%; }
          }
        `}</style>
        <div className="crd-shell">
          <div className="crd-header">
            <div className="crd-header-left">
              <div className="crd-icon-box">
                <i className="fa fa-cogs" />
              </div>
              <div className="crd-title-block">
                <h2 className="crd-title">Configure & Load Default Documents</h2>
                <p className="crd-subtitle">Select the required documents you want to load for this candidate, and verify their configurations below.</p>
                <div className="crd-meta-row">
                  <div className="crd-meta-pill">
                    <i className="fa fa-briefcase" /> Performer: {performerType || "Candidate"}
                  </div>
                  <div className="crd-meta-pill">
                    <i className="fa fa-hashtag" /> Flow: Onboarding Configuration
                  </div>
                </div>
              </div>
            </div>
            <button
              className="crd-close-btn"
              onClick={onHide}
              disabled={isProcessing}
              title={isProcessing ? "Please wait until processing finishes" : "Close"}
            >
              <i className="fa fa-times" />
            </button>
          </div>

          <div className="crd-body">
            <div className="crd-tabs-container">
              <div className="crd-tabs-list">
                <button
                  type="button"
                  className={`crd-tab-item ${activeTab === "documents" ? "active" : ""}`}
                  onClick={() => setActiveTab("documents")}
                >
                  <i className="fa fa-files-o" /> Documents
                  <span className="crd-tab-count">{tempDocuments.length}</span>
                </button>
                {actionTab && !actionTab.bulk && (
                  <button
                    type="button"
                    className={`crd-tab-item ${activeTab === "action" ? "active" : ""}`}
                    onClick={() => setActiveTab("action")}
                  >
                    <i className={
                      actionTab.type === "purchaseOrder" || actionTab.type === "purchaseOrderUpload"
                        ? "fa fa-shopping-cart"
                        : actionTab.type === "generate"
                          ? "fa fa-magic"
                          : "fa fa-paperclip"
                    } />
                    {actionTab.type === "purchaseOrder"
                      ? "Generate Purchase Order"
                      : actionTab.type === "purchaseOrderUpload"
                        ? "Upload Purchase Order"
                        : actionTab.type === "generate"
                          ? "Generate"
                          : "Upload Reference"}: {actionTab.doc.name}
                    <span
                      role="button"
                      tabIndex={0}
                      className="crd-tab-close"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCloseActionTab();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") handleCloseActionTab();
                      }}
                    >
                      <i className="fa fa-times" />
                    </span>
                  </button>
                )}
                <button
                  type="button"
                  className={`crd-tab-item ${activeTab === "add" ? "active" : ""}`}
                  disabled={bulkProcess.active}
                  onClick={() => {
                    setEditIndex(null);
                    setNewDocName("");
                    setNewDocAction("upload");
                    setNewDdrmsDocId("");
                    setNewMappingId("");
                    setNewReqDocId("");
                    setActiveTab("add");
                  }}
                >
                  {editIndex !== null ? (
                    <>
                      <i className="fa fa-pencil" /> Edit Document
                    </>
                  ) : (
                    <>
                      <i className="fa fa-plus-circle" /> Add New Document
                    </>
                  )}
                </button>
              </div>
            </div>

            {bulkProcess.status !== "idle" && bulkProcess.total > 0 && (
              <div className={`crd-bulk-progress ${bulkProcess.status}`}>
                <div className="crd-bulk-progress-head">
                  <span>
                    {bulkProcess.status === "running" && (
                      <><i className="fa fa-circle-o-notch fa-spin" /> Processing {bulkProcess.completed + 1} of {bulkProcess.total}: {actionTab?.doc?.name}</>
                    )}
                    {bulkProcess.status === "complete" && (
                      <><i className="fa fa-check-circle" /> All selected documents processed</>
                    )}
                    {bulkProcess.status === "cancelled" && (
                      <><i className="fa fa-exclamation-circle" /> Bulk processing cancelled after {bulkProcess.completed} of {bulkProcess.total}</>
                    )}
                  </span>
                  <span>{bulkProgressPercent}%</span>
                </div>
                <progress className="crd-bulk-progress-native" value={bulkProgressPercent} max="100" aria-label="Bulk document processing progress" />
              </div>
            )}

            {actionTab?.bulk && (
              <div className="crd-background-processor" aria-hidden="true">
                {actionTab.type === "purchaseOrder"
                  ? renderPurchaseOrder && renderPurchaseOrder(actionTab.doc, handleCloseActionTab, getGenerateRenderOptions(actionTab.doc, true))
                  : actionTab.type === "generate"
                    ? renderGenerate && renderGenerate(actionTab.doc, handleCloseActionTab, getGenerateRenderOptions(actionTab.doc, true))
                    : renderUploadReference && renderUploadReference(actionTab.doc, handleCloseActionTab, getUploadRenderOptions(actionTab.doc, true))}
              </div>
            )}

            {activeTab === "action" && actionTab && !actionTab.bulk ? (
              <div className="crd-action-tab-pane">
                <div className="crd-action-body-toolbar">
                  <button type="button" className="crd-btn crd-btn-sm" onClick={() => handleCloseActionTab(false)}>
                    <i className="fa fa-arrow-left" /> Back to Documents
                  </button>
                </div>
                {actionTab.type === "purchaseOrder" || actionTab.type === "purchaseOrderUpload"
                  ? renderPurchaseOrder && renderPurchaseOrder(
                    actionTab.doc,
                    handleCloseActionTab,
                    getGenerateRenderOptions(actionTab.doc, false),
                    actionTab.type === "purchaseOrderUpload" ? "upload" : "generate"
                  )
                  : actionTab.type === "generate"
                    ? renderGenerate && renderGenerate(actionTab.doc, handleCloseActionTab, getGenerateRenderOptions(actionTab.doc, false))
                    : renderUploadReference && renderUploadReference(actionTab.doc, handleCloseActionTab, getUploadRenderOptions(actionTab.doc, false))}
              </div>
            ) : activeTab !== "add" ? (
              <div className="tab-pane-list">
                {/* 1. Generate Documents Section */}
                {activeTab === "documents" && (
                  <div className="crd-section-panel">
                    <div className="crd-section-head">
                      <h3 className="section-heading crd-section-heading-reset">
                        <i className="fa fa-file-pdf-o" /> Generate Documents ({generateDocs.length})
                      </h3>
                      {eligibleGenerateDocs.length > 0 && (
                        <div
                          className={`crd-checkbox-row crd-select-all ${allGenerateSelected ? "selected" : ""} ${isProcessing ? "disabled" : ""}`}
                          onClick={handleToggleAllGenerate}
                        >
                          <span className="crd-checkbox-wrap">
                            <span className="crd-checkbox-custom" />
                          </span>
                          <span>Select All</span>
                        </div>
                      )}
                    </div>

                    {generateDocs.length > 0 ? (
                      <div className="pdm-doc-list">
                        {generateDocs.map(({ doc, idx }) => (
                          <div key={idx} className="pdm-doc-row pdm-doc-row-card">
                            <div className="pdm-doc-check crd-doc-check-center">
                              <div
                                className={`crd-checkbox-row crd-checkbox-trigger ${!isBulkEligible(doc) || isProcessing ? "disabled" : ""} ${isBulkEligible(doc) && selectedDocsToLoad.includes(idx) ? "selected" : ""}`}
                                onClick={() => isBulkEligible(doc) && handleToggleTempDoc(idx)}
                              >
                                <span className="crd-checkbox-wrap">
                                  <span className="crd-checkbox-custom" />
                                </span>
                              </div>
                            </div>
                            <div className="pdm-doc-file">
                              <span className="pdm-doc-file-icon"><i className="fa fa-file-pdf-o" /></span>
                              <div className="pdm-doc-file-main">
                                <div className="pdm-doc-title-row">
                                  <span className="pdm-doc-required-label">{doc.name}</span>
                                </div>
                                <div className="pdm-doc-meta">
                                  {isPurchaseOrderDocument(doc) ? (
                                    <span>
                                      <strong>Purchase Order For:</strong>{" "}
                                      {doc.purchaseOrderType === "recruiter" ? "Recruiter" : "Subcontractor"}
                                    </span>
                                  ) : (
                                    <>
                                      <span><strong>DDRMS Doc ID:</strong> {doc.ddrmsDocId || <span className="crd-not-linked">Not Linked</span>}</span>
                                      <span>•</span>
                                      <span><strong>Mapping ID:</strong> {doc.mappingId || <span className="crd-not-linked">Not Linked</span>}</span>
                                    </>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="pdm-doc-actions-panel crd-doc-actions">
                              {["generated", "uploaded"].includes(bulkItemStatuses[getBulkItemKey(
                                isPurchaseOrderDocument(doc) ? "purchaseOrder" : "generate",
                                doc,
                                idx
                              )]) && (
                                  <span className="crd-row-status">
                                    <i className="fa fa-check-circle" />
                                    {bulkItemStatuses[getBulkItemKey(
                                      isPurchaseOrderDocument(doc) ? "purchaseOrder" : "generate",
                                      doc,
                                      idx
                                    )] === "uploaded" ? "Uploaded" : "Generated"}
                                  </span>
                                )}
                              {!["generated", "uploaded"].includes(bulkItemStatuses[getBulkItemKey(
                                isPurchaseOrderDocument(doc) ? "purchaseOrder" : "generate",
                                doc,
                                idx
                              )]) && stagedGeneratedDocs[getGeneratedKey(doc)] && (
                                  <span className="crd-row-status staged">
                                    <i className={getStagedPurchaseOrder(doc)?.kind === "uploaded" ? "fa fa-cloud-upload" : "fa fa-file-pdf-o"} />
                                    {getStagedPurchaseOrder(doc)?.kind === "uploaded" ? "Uploaded Ready" : "Generated Ready"}
                                  </span>
                                )}
                              {isPurchaseOrderDocument(doc) && renderPurchaseOrder ? (
                                <>
                                  <button
                                    type="button"
                                    className="crd-btn crd-btn-sm crd-action-btn"
                                    onClick={() => handleOpenActionTab("purchaseOrder", doc)}
                                    disabled={getStagedPurchaseOrder(doc)?.kind === "uploaded" || isProcessing}
                                    title="Generate Purchase Order"
                                  >
                                    <i className="fa fa-magic" /> Generate
                                  </button>
                                  <button
                                    type="button"
                                    className="crd-btn crd-btn-sm crd-action-btn"
                                    onClick={() => handleOpenActionTab("purchaseOrderUpload", doc)}
                                    disabled={getStagedPurchaseOrder(doc)?.kind === "generated" || isProcessing}
                                    title="Upload Purchase Order"
                                  >
                                    <i className="fa fa-cloud-upload" /> Upload PO
                                  </button>
                                  {getStagedPurchaseOrder(doc) && (
                                    <button
                                      type="button"
                                      className="crd-btn crd-btn-sm crd-btn-ghost-danger crd-action-btn"
                                      onClick={() => handleRemoveStagedPurchaseOrder(doc, idx)}
                                      disabled={isProcessing}
                                      title="Remove staged Purchase Order"
                                    >
                                      <i className="fa fa-times" /> Remove PO
                                    </button>
                                  )}
                                </>
                              ) : (
                                <button
                                  type="button"
                                  className="crd-btn crd-btn-sm crd-action-btn"
                                  onClick={() => handleOpenActionTab("generate", doc)}
                                  disabled={!isGenerateReady(doc) || isProcessing}
                                  title="Generate Document"
                                >
                                  <i className="fa fa-magic" /> Generate
                                </button>
                              )}
                              <button
                                type="button"
                                className="crd-btn crd-btn-sm crd-btn-ghost-indigo crd-icon-action-btn"
                                onClick={() => handleEditDocClick(idx)}
                                disabled={isProcessing}
                                title="Edit Document"
                              >
                                <i className="fa fa-pencil" />
                              </button>
                              <button
                                type="button"
                                className="crd-btn crd-btn-sm crd-btn-ghost-danger crd-icon-action-btn"
                                onClick={() => handleDeleteDoc(idx)}
                                disabled={isProcessing}
                                title="Remove Document"
                              >
                                <i className="fa fa-trash" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="crd-empty-config">
                        <i className="fa fa-file-text-o crd-empty-config-icon" />
                        <span className="crd-empty-config-text">No generated documents configured.</span>
                      </div>
                    )}
                  </div>
                )}

                {/* 2. Upload Documents Section */}
                {activeTab === "documents" && (
                  <div className="crd-section-panel">
                    <div className="crd-section-head">
                      <h3 className="section-heading crd-section-heading-reset">
                        <i className="fa fa-cloud-upload" /> Require Documents ({uploadDocs.length})
                      </h3>
                      {eligibleUploadDocs.length > 0 && (
                        <div
                          className={`crd-checkbox-row crd-select-all ${allUploadSelected ? "selected" : ""} ${isProcessing ? "disabled" : ""}`}
                          onClick={handleToggleAllUpload}
                        >
                          <span className="crd-checkbox-wrap">
                            <span className="crd-checkbox-custom" />
                          </span>
                          <span>Select All</span>
                        </div>
                      )}
                    </div>

                    {uploadDocs.length > 0 ? (
                      <div className="pdm-doc-list">
                        {uploadDocs.map(({ doc, idx }) => (
                          <div key={idx} className="pdm-doc-row pdm-doc-row-card">
                            <div className="pdm-doc-check crd-doc-check-center">
                              <div
                                className={`crd-checkbox-row crd-checkbox-trigger ${!doc.reqDocId || isProcessing ? "disabled" : ""} ${doc.reqDocId && selectedDocsToLoad.includes(idx) ? "selected" : ""}`}
                                onClick={() => doc.reqDocId && handleToggleTempDoc(idx)}
                              >
                                <span className="crd-checkbox-wrap">
                                  <span className="crd-checkbox-custom" />
                                </span>
                              </div>
                            </div>
                            <div className="pdm-doc-file">
                              <span className="pdm-doc-file-icon"><i className="fa fa-list-alt" /></span>
                              <div className="pdm-doc-file-main">
                                <div className="pdm-doc-title-row">
                                  <span className="pdm-doc-required-label">{doc.name}</span>
                                </div>
                                <div className="pdm-doc-meta">
                                  <span><strong>Required Doc ID:</strong> {doc.reqDocId || <span className="crd-not-linked">Not Linked</span>}</span>
                                </div>
                              </div>
                            </div>
                            <div className="pdm-doc-actions-panel crd-doc-actions">
                              {bulkItemStatuses[getBulkItemKey("upload", doc, idx)] === "uploaded" && (
                                <span className="crd-row-status"><i className="fa fa-check-circle" /> Uploaded</span>
                              )}
                              {bulkItemStatuses[getBulkItemKey("upload", doc, idx)] !== "uploaded" && stagedReferenceDocs[getReferenceKey(doc)] && (
                                <span className="crd-row-status staged"><i className="fa fa-paperclip" /> Reference Ready</span>
                              )}
                              <button
                                type="button"
                                className="crd-btn crd-btn-sm crd-action-btn"
                                onClick={() => handleOpenActionTab("upload", doc)}
                                disabled={!doc.reqDocId || isProcessing}
                                title="Upload Reference Document"
                              >
                                <i className="fa fa-paperclip" /> Upload Reference Doc
                              </button>
                              <button
                                type="button"
                                className="crd-btn crd-btn-sm crd-btn-ghost-indigo crd-icon-action-btn"
                                onClick={() => handleEditDocClick(idx)}
                                disabled={isProcessing}
                                title="Edit Document"
                              >
                                <i className="fa fa-pencil" />
                              </button>
                              <button
                                type="button"
                                className="crd-btn crd-btn-sm crd-btn-ghost-danger crd-icon-action-btn"
                                onClick={() => handleDeleteDoc(idx)}
                                disabled={isProcessing}
                                title="Remove Document"
                              >
                                <i className="fa fa-trash" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="crd-empty-config">
                        <i className="fa fa-cloud-upload crd-empty-config-icon" />
                        <span className="crd-empty-config-text">No require documents configured.</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="tab-pane-add-doc crd-add-tab">
                <div className="crd-section-panel crd-add-panel">
                  <h3 className="section-heading crd-add-heading">
                    <i className="fa fa-pencil" /> {editIndex !== null ? "Edit Onboarding Document" : "Register New Onboarding Document"}
                  </h3>
                  <p className="crd-add-intro">
                    {editingPurchaseOrder
                      ? "Update the Purchase Order display name below."
                      : editIndex !== null
                        ? "Modify the properties of the selected document below."
                        : "Provide the document name and document type below. Once added, it will populate in the checklist."}
                  </p>
                  <form onSubmit={handleSaveDoc} className="crd-add-form">
                    <div className={`crd-add-main-grid ${editingPurchaseOrder ? "name-only" : ""}`}>
                      <div className="crd-form-group crd-form-group-compact">
                        <label className="crd-label">Document Name</label>
                        <span className="crd-form-help">The display name shown on candidate's checklist</span>
                        <input
                          type="text"
                          className="form-control crd-form-control-compact"
                          value={newDocName}
                          onChange={(e) => setNewDocName(e.target.value)}
                          placeholder="e.g. Non-Disclosure Agreement"
                          required
                        />
                      </div>
                      {!editingPurchaseOrder && (
                        <div className="crd-form-group crd-form-group-compact">
                          <label className="crd-label">Document Type</label>
                          <span className="crd-form-help">Action behavior</span>
                          <select
                            className="form-control crd-form-control-compact"
                            value={newDocAction}
                            onChange={(e) => setNewDocAction(e.target.value)}
                          >
                            <option value="upload">Require Document</option>
                            <option value="generate">Generate</option>
                          </select>
                        </div>
                      )}
                    </div>

                    {!editingPurchaseOrder && (newDocAction === "generate" ? (
                      <div className="crd-linked-fields-grid">
                        <div className="crd-form-group crd-form-group-compact">
                          <label className="crd-label">DDRMS Doc ID</label>
                          <span className="crd-form-help">Select template from DDRMS Repository</span>
                          <select
                            className="form-control crd-form-control-compact"
                            value={newDdrmsDocId}
                            onChange={(e) => setNewDdrmsDocId(e.target.value)}
                          >
                            <option value="">-- Select DDRMS Template --</option>
                            {ddrmsDocs.filter(doc => doc.fileExtension === "docx").map((doc) => (
                              <option key={doc.id} value={doc.id}>
                                {doc.name} (ID: {doc.id})
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="crd-form-group crd-form-group-compact">
                          <label className="crd-label">Mapping ID</label>
                          <span className="crd-form-help">Select field placeholder mapping profile</span>
                          <select
                            className="form-control crd-form-control-compact"
                            value={newMappingId}
                            onChange={(e) => setNewMappingId(e.target.value)}
                          >
                            <option value="">-- Select Mapping Profile --</option>
                            {dataTypeMappings.map((mapping) => (
                              <option key={mapping.id} value={mapping.id}>
                                {mapping.name}{mapping.type ? ` (${mapping.type})` : ""} (ID: {mapping.id})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ) : (
                      <div className="crd-linked-field-section">
                        <div className="crd-form-group crd-required-doc-field">
                          <label className="crd-label">Required Document ID</label>
                          <span className="crd-form-help">Select database required document to link</span>
                          <select
                            className="form-control crd-form-control-compact"
                            value={newReqDocId}
                            onChange={(e) => setNewReqDocId(e.target.value)}
                          >
                            <option value="">-- Select Required Document --</option>
                            {reqDocuments.map((doc) => (
                              <option key={doc.id} value={doc.id}>
                                {doc.name} (ID: {doc.id})
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    ))}

                    <div className="crd-add-actions">
                      <button type="submit" className="crd-btn primary crd-submit-btn">
                        <i className={editIndex !== null ? "fa fa-save" : "fa fa-plus"} /> {editIndex !== null ? "Update Document" : "Add Document to Checklist"}
                      </button>
                      <button
                        type="button"
                        className="crd-btn"
                        onClick={() => {
                          setEditIndex(null);
                          setNewDocName("");
                          setNewDdrmsDocId("");
                          setNewMappingId("");
                          setNewReqDocId("");
                          setActiveTab("documents");
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>

          <div className="crd-footer">
            <button className="crd-btn" onClick={onHide} disabled={isProcessing}>
              <i className="fa fa-times" /> Cancel
            </button>
            <button
              type="button"
              className="crd-btn primary"
              onClick={handleStartBulkUpload}
              disabled={selectedDocsToLoad.length === 0 || bulkProcess.active}
            >
              {bulkProcess.active ? (
                <><i className="fa fa-circle-o-notch fa-spin" /> Processing {bulkProcess.completed + 1}/{bulkProcess.total}</>
              ) : (
                <><i className="fa fa-cloud-upload" /> Upload All Selected</>
              )}
            </button>
            <button
              className="crd-btn primary"
              onClick={handleSave}
              disabled={isProcessing}
            >
              <i className="fa fa-save" /> Save Config
            </button>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default DefaultDocumentsModal;
